import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useStore } from "react-redux"

import ModalService from "src/modals"
import { selectGameConfig } from "src/store/common"
import { selectViewerId } from "src/store/game"
import { foundersWanted } from "src/tutorial/scenario"
import { SELECTORS, pointerTargetFor } from "src/tutorial/pointer"
import * as server from "src/tutorial/server"
import { viewWidth } from "src/components/Canvas"
import { activeWorld } from "src/utils/world"

import "./TutorialPointer.css"

/* The hand that points at the next press (owner's ask, 2026-09-25).

   `tutorial/pointer.js` decides *what* — a control by selector, or a cell on the map. This is the
   half that needs a browser: find it, measure it, and draw a ring round it with a hand beside it
   pointing in. Three things about how it does that, and each is a choice made against a worse
   one:

   - **It measures every frame, from the document, and nothing is subscribed to.** The targets
     live in five different places — the store, a modal's own state, the canvas's camera, a
     picker's component state, a number field — and a subscription to each is a coupling to each.
     One `requestAnimationFrame` loop that asks "where is it now?" costs a `querySelector` and a
     rectangle a frame, follows a sheet sliding in and the map being dragged for free, and cannot
     go stale. Writes to the DOM are skipped when nothing moved, so an idle frame is one string
     comparison.
   - **It draws through a portal, over everything.** The lesson mounts under a route that
     `AnimatePresence` transforms while it fades, and a `position: fixed` box inside a transformed
     ancestor is fixed to the ancestor, not the window. On `body` it is fixed to the window, and
     above the modal layer (1000) so a control inside a sheet can be pointed at.
   - **It never takes a press.** `pointer-events: none` on the whole of it: a hand that swallowed
     the click it was asking for would be the worst control on the screen.

   A cell is found through the camera the canvas stashes on its own element (`canvas.camera`, the
   centre of the view in world cells and the cell size, refreshed on every move), the same way it
   stashes `viewScale`. Where a target is under a sheet — END TURN behind the phone's full-screen
   city view — the hand points at the sheet's close button instead, because a press on a covered
   control is a press on the sheet. */

// The hand and its arrow need this much room beside the target; the first side that has it wins.
const ROOM_PX = 84
// Between the target's edge and the hand's.
const GAP_PX = 10
const SIDES = ["below", "above", "right", "left"]
const GLYPHS = { below: "👆", above: "👇", right: "👈", left: "👉" }

/* The shortest signed distance between two columns on a cylinder, in cells — the way
   `Canvas.wrapCol` and the server's `column_distance` read it. */
const wrappedDelta = (delta) => {
  const COLUMNS = activeWorld().columns
  const half = COLUMNS / 2
  return ((((delta + half) % COLUMNS) + COLUMNS) % COLUMNS) - half
}

/* Whether the point at the middle of a box is the element's own — or, for a cell, the map's.
   `elementFromPoint` is missing in jsdom and answers null outside the window; both read as
   reachable, because a thing that cannot be checked should not be hidden for it. */
const reachable = (element, box) => {
  if (typeof document.elementFromPoint !== "function") return true
  const hit = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2)
  if (!hit) return true
  return element.contains(hit) || hit.contains(element)
}

const union = (rects) => {
  const boxes = rects.filter((rect) => rect.width && rect.height)
  if (!boxes.length) return null
  const left = Math.min(...boxes.map((rect) => rect.left))
  const top = Math.min(...boxes.map((rect) => rect.top))
  const right = Math.max(...boxes.map((rect) => rect.right))
  const bottom = Math.max(...boxes.map((rect) => rect.bottom))
  return { left, top, width: right - left, height: bottom - top }
}

/* An element's box — or, for one that has none of its own (`display: contents`, which is what
   the march order's unit buttons are), the box round what it holds. */
const boxOf = (element) => {
  const rect = element.getBoundingClientRect()
  if (rect.width && rect.height) return { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
  return union([...element.children].map((child) => child.getBoundingClientRect()))
}

/* The first match that is drawn. The phone keeps the desktop's docks mounted and hidden, so the
   round report — battle line and all — is in the document twice there, and the first copy has no
   box: read as "the battle line is here", it hid the hand for the whole battle step. */
const visible = (selector) =>
  [...document.querySelectorAll(selector)].find((element) => boxOf(element)) || null

const elementBox = (selector) => {
  const element = visible(selector)
  const box = element && boxOf(element)
  return box ? { ...box, element, covered: !reachable(element, box) } : null
}

/* What to scroll to reach an element: itself, or — for one with no box of its own — the first
   child that has one. `scrollIntoView` on a `display: contents` element is a no-op, which on a
   phone left the march order's worker row below the fold for ever, read as covered, and had the
   hand asking for the sheet to be closed and then opened again, round and round. */
const scrollNode = (element) => {
  if (element.getBoundingClientRect().width) return element
  return [...element.children].find((child) => child.getBoundingClientRect().width) || element
}

/* The visible part of the top sheet, when the element is inside it. Being inside the top sheet
   means the sheet cannot be what covers it — a "covered" reading there is the sheet's own
   scroller clipping it, and the answer is to scroll or to point from the sheet's edge, never to
   close the sheet. */
const sheetAround = (element) => {
  const content = element?.closest(".modal-layer:not(.modal-layer-hidden) .modal-content")
  return content ? content.getBoundingClientRect() : null
}

const clampInto = (box, rect) => ({
  ...box,
  left: Math.min(Math.max(box.left, rect.left), Math.max(rect.left, rect.right - box.width)),
  top: Math.min(Math.max(box.top, rect.top), Math.max(rect.top, rect.bottom - box.height)),
})

const offScreen = (box) =>
  box.top < 0 || box.left < 0 || box.top + box.height > window.innerHeight || box.left + box.width > window.innerWidth

const cellBox = (at) => {
  const canvas = document.querySelector(".game-map canvas")
  const camera = canvas?.camera
  if (!camera) return null
  const rect = canvas.getBoundingClientRect()
  if (!rect.width || !rect.height) return null
  // The canvas is stretched to its box by CSS; its camera counts in the drawing buffer's pixels.
  const view = viewWidth(canvas)
  const scale = view ? rect.width / view : 1
  const size = camera.squareSize * scale
  const [column, row] = at.split("-").map(Number)
  const centreX = rect.left + rect.width / 2 + wrappedDelta(column + 0.5 - camera.col) * size
  const centreY = rect.top + rect.height / 2 + (row + 0.5 - camera.row) * size
  // A cell the player has scrolled away from is pointed at from the map's edge, not from nowhere.
  const half = size / 2
  const x = Math.min(Math.max(centreX, rect.left + half), rect.right - half)
  const y = Math.min(Math.max(centreY, rect.top + half), rect.bottom - half)
  const box = { left: x - half, top: y - half, width: size, height: size }
  return { ...box, covered: !reachable(canvas, box) }
}

// Roughly the hand's own extent along the axis it points on: the arrow, the glyph and the gap.
const HAND_PX = 64
// What the hand must not sit on: another control, a line of text, or the lesson's own words.
const NOT_UNDER_THE_HAND = "button, [role='button'], input, select, textarea, a, label, p, h1, h2, h3, h4, h5, h6, .coach"

/* Where the hand's middle would land on each side. */
const handCentre = (box, side) => {
  const centreX = box.left + box.width / 2
  const centreY = box.top + box.height / 2
  const reach = GAP_PX + HAND_PX / 2
  return {
    below: [centreX, box.top + box.height + reach],
    above: [centreX, box.top - reach],
    right: [box.left + box.width + reach, centreY],
    left: [box.left - reach, centreY],
  }[side]
}

const clearAt = ([x, y]) => {
  if (typeof document.elementFromPoint !== "function") return true
  const hit = document.elementFromPoint(x, y)
  return !hit || !hit.closest(NOT_UNDER_THE_HAND)
}

/* The side the hand goes on: the first with room for it that does not put it over another
   control — a hand over the button beside the one it means is a hand pointing at two things.
   With no clear side, the first with room; with none, below. */
const sideFor = (box) => {
  const room = {
    below: window.innerHeight - (box.top + box.height),
    above: box.top,
    right: window.innerWidth - (box.left + box.width),
    left: box.left,
  }
  const fitting = SIDES.filter((side) => room[side] >= ROOM_PX)
  return fitting.find((side) => clearAt(handCentre(box, side))) || fitting[0] || "below"
}

const TutorialPointer = () => {
  const store = useStore()
  const root = useRef(null)
  const ring = useRef(null)
  const hand = useRef(null)
  const glyph = useRef(null)
  const badge = useRef(null)
  // What was last written, so a frame that changes nothing touches nothing.
  const drawn = useRef("")
  // The last target, so a control below a sheet's fold is scrolled to once, when it becomes
  // the target — and never again while it is, or the player could not scroll the sheet at all.
  const aimed = useRef("")

  useEffect(() => {
    let frame = null

    const probe = {
      exists: (selector) => Boolean(visible(selector)),
      value: (selector) => visible(selector)?.value ?? null,
    }

    const hide = () => {
      if (drawn.current === "hidden") return
      drawn.current = "hidden"
      root.current?.setAttribute("hidden", "")
    }

    const show = (box, side, count) => {
      const key = [side, box.left, box.top, box.width, box.height].map(Math.round).join(",") + `:${count ?? ""}`
      if (drawn.current === key) return
      drawn.current = key
      const node = root.current
      if (!node) return
      node.removeAttribute("hidden")
      ring.current.style.left = `${box.left - 4}px`
      ring.current.style.top = `${box.top - 4}px`
      ring.current.style.width = `${box.width + 8}px`
      ring.current.style.height = `${box.height + 8}px`
      const centreX = box.left + box.width / 2
      const centreY = box.top + box.height / 2
      const anchor = {
        below: [centreX, box.top + box.height + GAP_PX],
        above: [centreX, box.top - GAP_PX],
        right: [box.left + box.width + GAP_PX, centreY],
        left: [box.left - GAP_PX, centreY],
      }[side]
      hand.current.style.left = `${anchor[0]}px`
      hand.current.style.top = `${anchor[1]}px`
      hand.current.dataset.side = side
      glyph.current.textContent = GLYPHS[side]
      // The number to type, on the hand itself: the press it asks for is "type this here".
      if (count == null) badge.current.setAttribute("hidden", "")
      else {
        badge.current.textContent = String(count)
        badge.current.removeAttribute("hidden")
      }
    }

    const tick = () => {
      frame = window.requestAnimationFrame(tick)
      const run = server.snapshot()
      if (!run || run.finished) return hide()
      const state = store.getState()
      const target = pointerTargetFor({
        step: run.step,
        clear: run.clear,
        acted: run.acted,
        ui: state.ui,
        modals: ModalService.stackTypes || [],
        founders: foundersWanted(selectGameConfig(state), run.game, selectViewerId(state)) || 0,
        probe,
      })
      if (!target) return hide()
      let box = target.kind === "cell" ? cellBox(target.at) : elementBox(target.selector)
      // A control past the fold of a scrolling sheet is brought into view — once, on becoming
      // the target — rather than pointed at from outside the window. Not a covered one: that is
      // a sheet over it, which scrolling does not fix, and the fallback below does.
      const aim = target.kind === "cell" ? target.at : target.selector
      if (aim !== aimed.current) {
        aimed.current = aim
        // Clipped by the sheet's own scroller reads as covered too: the point at its middle
        // belongs to whatever is drawn under the sheet's edge. Scrolled, and measured again
        // next frame rather than drawn where it was.
        if (box?.element && (offScreen(box) || box.covered)) {
          scrollNode(box.element).scrollIntoView({ block: "center", inline: "nearest" })
          return undefined
        }
      }
      if (box?.covered) {
        const sheet = sheetAround(box.element)
        // Inside the top sheet and clipped by it (the player scrolled away): pointed at from
        // the sheet's edge, the way a cell off the map is pointed at from the map's.
        if (sheet) box = { ...clampInto(box, sheet), covered: false }
        else if (ModalService.hasOpenModals()) box = elementBox(SELECTORS.dismiss) || box
        // Under a phone sheet (the round report left open on the attack step, owner 2026-09-28):
        // the ring was drawn round a map cell nobody could see, with the hand over the report.
        // The sheet's close is the press that uncovers it.
        else if (!box.element?.closest(".sheet")) box = elementBox(SELECTORS.closeSheet) || box
      }
      if (!box) return hide()
      return show(box, sideFor(box), target.count)
    }

    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [store])

  return createPortal(
    <div ref={root} className="tut-pointer" aria-hidden="true" hidden>
      <div ref={ring} className="tut-pointer-ring" />
      <div ref={hand} className="tut-pointer-hand" data-side="below">
        <span className="tut-pointer-bob">
          <svg className="tut-pointer-arrow" viewBox="0 0 24 24" width="22" height="22" focusable="false">
            <path d="M12 22V5M5 12l7-7 7 7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span ref={glyph} className="tut-pointer-glyph">
            👆
          </span>
          <span ref={badge} className="tut-pointer-count" hidden />
        </span>
      </div>
    </div>,
    document.body
  )
}

export default TutorialPointer
